import { useEffect, useId, useState } from 'react';
import { apiCall } from '../api/client';
import { inputCls } from './LessonEditorFields';

export default function OpenRouterModelPicker({ courseId, token, value, onChange, disabled }) {
  const id = useId();
  const [models, setModels] = useState([]);
  const [defaultModel, setDefaultModel] = useState('');
  const [search, setSearch] = useState('');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [attempt, setAttempt] = useState(0);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setError('');
    apiCall('GET', `/courses/${courseId}/lesson-planner/models`, null, token)
      .then(data => {
        if (!Array.isArray(data.models) || !data.models.length) throw new Error('No OpenRouter models are available right now.');
        if (!cancelled) { setModels(data.models); setDefaultModel(data.defaultModel || ''); }
      })
      .catch(err => { if (!cancelled) setError(err.message || 'Could not load OpenRouter models.'); })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [courseId, token, attempt]);

  const query = search.trim().toLowerCase();
  const matches = models.filter(model => `${model.name} ${model.id}`.toLowerCase().includes(query));
  // Searching must not silently change the selected model or hide its option.
  const options = models.filter(model => model.id === value || matches.includes(model));
  return <div className="space-y-2">
    <label htmlFor={id} className="block text-sm font-medium text-amber-700">OpenRouter model</label>
    <div className="grid grid-cols-1 sm:grid-cols-3 gap-2">
      <input type="search" aria-label="Search OpenRouter models" value={search} onChange={e => setSearch(e.target.value)}
        placeholder="Search models…" disabled={disabled || loading || !models.length} className={inputCls} />
      <select id={id} value={value} onChange={e => onChange(e.target.value)} disabled={disabled || loading}
        aria-describedby={`${id}-hint`} className={`${inputCls} bg-white sm:col-span-2 min-w-0`}>
        <option value="">{defaultModel ? `Default (${defaultModel})` : 'Configured default'}</option>
        {value && !models.some(model => model.id === value) && <option value={value}>{value}</option>}
        {options.map(model => <option key={model.id} value={model.id}>{model.name} — {model.id}</option>)}
      </select>
    </div>
    <p id={`${id}-hint`} className="text-xs text-amber-600">
      {loading ? 'Loading OpenRouter models…' : 'Used for your next message. Switching models keeps this conversation.'}
    </p>
    {!loading && query && !matches.length && <p className="text-xs text-amber-600">No matching models. Try another search.</p>}
    {error && <p role="alert" className="text-xs text-red-600">
      {error} You can still use the configured default.{' '}
      <button type="button" onClick={() => setAttempt(n => n + 1)} disabled={loading} className="underline">Retry models</button>
    </p>}
  </div>;
}
